import { Component, OnInit, AfterViewInit, OnDestroy, signal, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import * as XLSX from 'xlsx';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { Chart, registerables } from 'chart.js';
import { FirestoreProjectService } from '../../services/firestore-project.service';
import { DashboardStatsService } from '../../services/dashboard-stats.service';
import { MMSYTRIPService } from '../../services/mmsy-trip.service';
import { AuthService } from '../../services/auth.service';
import { UserRole } from '../../models/user.model';
import { Project, ProjectStats, RDSchemeData } from '../../models/project.model';

import { DepartmentSchemesService } from '../../services/department-schemes.service';
import { PhotoUploadService } from '../../services/photo-upload.service';
import { DepartmentMasterService } from '../../services/department-master.service';
import { FirestoreDepartmentService } from '../../services/firestore-department.service';
import { DepartmentMatchingService } from '../../services/department-matching.service';
import { resolveHmDepartmentName } from '../../data/hm-department-master';
import { Department } from '../../models/user.model';

Chart.register(...registerables);

interface ExcelImportRow {
    name: string;
    dept: string;
    loc: string;
    scheme: string;
    cost: number;
    spent: number;
    physical: number;
    status: string;
    start: string;
    end: string;
    priority: boolean;
    remarks: string;
    executingAgency: string;
    _sheet: string;
    _valid: boolean;
    _errors: string[];
}

@Component({
    selector: 'app-dashboard',
    imports: [CommonModule, RouterModule, ReactiveFormsModule],
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
    /** KPI stats — auto-derived from visibleProjects (minister-scoped). */
    stats = computed<ProjectStats>(() => {
        const p = this.visibleProjects();
        return {
            totalProjects:   p.length,
            totalSanctioned: p.reduce((s, x) => s + (x.cost ?? 0), 0),
            totalSpent:      p.reduce((s, x) => s + (x.spent ?? 0), 0),
            stuckCount:      p.filter(x => x.status === 'Stuck').length,
            avgProgress:     p.length
                ? Math.round(p.reduce((s, x) => s + (x.physical ?? 0), 0) / p.length)
                : 0
        };
    });

    allProjects = signal<Project[]>([]);
    priorityProjects = signal<Project[]>([]);
    highVisibilityProjects = signal<Project[]>([]);
    hmCommittedProjects = signal<Project[]>([]);

    private isMinisterRole = computed<boolean>(() =>
        this.authService.currentUser()?.role === UserRole.MINISTER
    );

    private escalatedFilter(p: Project): boolean {
        return !!p.hmPriority || !!p.recurringIntervention || !!(p as any).isDelayed;
    }

    visibleProjects = computed<Project[]>(() =>
        this.isMinisterRole()
            ? this.allProjects().filter(p => this.escalatedFilter(p))
            : this.allProjects()
    );

    // Checkbox flag signals for real-time OnPush-safe styling in the create modal
    hmPriorityFlag = signal(false);
    recurringFlag = signal(false);
    rdSchemeData = computed<RDSchemeData[]>(() => {
        const rdProjects = this.visibleProjects().filter(p => p.dept === 'RD');
        const schemeMap = new Map<string, RDSchemeData>();
        
        for (const p of rdProjects) {
            const schemeName = p.scheme || 'Other RD Schemes';
            const cur = schemeMap.get(schemeName) || {
                schemeName, count: 0, totalCost: 0, totalSpent: 0, totalRoadLength: 0,
                statusBreakdown: { 'Completed': 0, 'In Progress': 0, 'Stuck': 0, 'Planned': 0 }
            };
            cur.count++;
            cur.totalCost += (p.cost || 0);
            cur.totalSpent += (p.spent || 0);
            cur.totalRoadLength += (p.roadLength || 0);
            if (p.status) cur.statusBreakdown[p.status]++;
            schemeMap.set(schemeName, cur);
        }
        return Array.from(schemeMap.values());
    });

    departmentBreakdown = signal<any[]>([]);
    firestoreDepts = signal<Department[]>([]);

    /**
     * Delayed count for the KPI card — only projects OSD explicitly marked isDelayed=true.
     * Reads from DashboardStatsService (single source of truth shared with the page).
     */
    readonly delayedCardCount = computed(() => this.dashStats.explicitlyDelayedCount());

    /** Most recent updatedAt timestamp across all visible projects. */
    private readonly lastProjectUpdate = computed(() => {
        let latest: Date | null = null;
        for (const p of this.visibleProjects()) {
            const raw = (p as any).updatedAt;
            if (!raw) continue;
            const d: Date = raw?.toDate ? raw.toDate() : new Date(raw);
            if (!latest || d > latest) latest = d;
        }
        return latest;
    });

    /** Relative time since the last project edit — re-evaluates every minute. */
    readonly lastSyncDisplay = computed(() => {
        const update = this.lastProjectUpdate();
        if (!update) return 'No updates';
        const now  = this.currentTime();
        const mins = Math.floor((now.getTime() - update.getTime()) / 60000);
        if (mins < 1)  return 'Just now';
        if (mins < 60) return `${mins} min ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24)  return `${hrs} hr ago`;
        return `${Math.floor(hrs / 24)}d ago`;
    });

    readonly lastSyncTime = computed(() => {
        const update = this.lastProjectUpdate();
        if (!update) return '—';
        return update.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    });

    /** Count of In Progress + Completed projects (active spend). */
    readonly expenditureProjectCount = computed(() =>
        this.visibleProjects().filter(p =>
            p.status === 'In Progress' || p.status === 'Completed'
        ).length
    );

    /** Total spent across In Progress + Completed projects only (values in Lakhs). */
    readonly expenditureTotalSpent = computed(() =>
        this.visibleProjects()
            .filter(p => p.status === 'In Progress' || p.status === 'Completed')
            .reduce((sum, p) => sum + (p.spent ?? 0), 0)
    );

    /**
     * Format a Lakh-based amount for the KPI card.
     * < 100 L  → "₹20 L"
     * >= 100 L → "₹5.2 Cr"
     */
    formatSpent(lakhs: number): string {
        if (!lakhs) return '₹0 L';
        if (lakhs >= 100) {
            const cr = lakhs / 100;
            return '₹' + (Number.isInteger(cr) ? cr : cr.toFixed(1)) + ' Cr';
        }
        return '₹' + Math.round(lakhs) + ' L';
    }

    formatKpiCount(value: number): string {
        const n = Math.max(0, Math.round(value || 0));
        return n < 10 ? String(n).padStart(2, '0') : String(n);
    }

    navigateToDelayed(): void {
        this.router.navigate(['/dhamnagar-dashboard/delayed-projects']);
    }

    // ── Metrics for Alert Bar ──
    delayedMoreThan60 = computed(() => {
        if (this.isMinisterRole()) {
            return this.visibleProjects().filter(p => !!(p as any).isDelayed).length;
        }
        const today = new Date();
        return this.allProjects().filter(p => {
            if (p.status === 'Completed') return false;
            if (!!(p as any).isDelayed) return true;
            const target = new Date(p.targetDate || p.end);
            const diffDays = Math.floor((today.getTime() - target.getTime()) / (1000 * 3600 * 24));
            return diffDays > 60;
        }).length;
    });

    pendingHmReview = computed(() => {
        return this.visibleProjects().filter(p => p.hmPriority && (p.physical < 50 || p.status === 'Stuck')).length;
    });

    lowProgressProjects = computed(() => {
        return this.visibleProjects().filter(p => p.status !== 'Completed' && p.status !== 'Planned' && p.physical < 30).length;
    });

    // ── HM Summary Widget ──
    hmStats = computed(() => {
        const hm = this.visibleProjects().filter(p =>
            p.priority || p.hmPriority || p.recurringIntervention ||
            p.visibility === 'HM Committed' || p.visibility === 'High Visibility'
        );

        const totalCount = hm.length;
        
        // Mutually exclusive logic:
        // 1. Delayed: explicitly flagged by OSD, Stuck status, or past target date
        const delayedProjects = hm.filter(p =>
            !!(p as any).isDelayed ||
            p.status === 'Stuck' ||
            (p.targetDate && new Date(p.targetDate) < new Date()) ||
            (p.end && new Date(p.end) < new Date())
        );
        const delayed = delayedProjects.length;

        // 2. On Track: Not delayed AND physical progress >= 70%
        const onTrack = hm.filter(p => 
            !delayedProjects.includes(p) && 
            (p.physical >= 70 || p.status === 'Completed')
        ).length;

        // 3. At Risk: Rest of the projects
        const atRisk = Math.max(0, totalCount - onTrack - delayed);
        
        const total = totalCount || 1; 
        return {
            total: totalCount,
            onTrack,
            onTrackPct: Math.round((onTrack / total) * 100),
            atRisk,
            atRiskPct: Math.round((atRisk / total) * 100),
            delayed,
            delayedPct: Math.round((delayed / total) * 100)
        };
    });


    // ── Progress Overview Widget ──
    progressStats = computed(() => {
        const projects = this.visibleProjects();
        const total = projects.length || 1;
        const done = projects.filter(p => p.status === 'Completed').length;
        const active = projects.filter(p => p.status === 'In Progress').length;
        const slow = projects.filter(p => p.status === 'Stuck').length;
        const planned = projects.filter(p => p.status === 'Planned').length;
        
        const avgPhys = projects.length > 0
            ? (projects.reduce((s, p) => s + (p.physical || 0), 0) / projects.length).toFixed(1)
            : '0.0';

        return { done, active, slow, planned, avgPhys };
    });

    private static deptNameToId(name: string): string {
        return name.toLowerCase().replace(/[\s&]/g, '_').replace(/[^a-z0-9_]/g, '').replace(/_+/g, '_');
    }

  /** Live department rollup — same rules & data as the departments detail page. */
  allDeptRows = computed(() => {
    const currentUser = this.authService.currentUser();
    const projects = this.visibleProjects();
    const today = new Date();
    let deptEntries = this.deptSchemesService.getDepartments();

    if (currentUser?.role === UserRole.OPERATOR && currentUser.departmentIds?.length) {
      const allowedIds = new Set(currentUser.departmentIds);
      const fsDepts = this.firestoreDepts();
      const allowedNames = new Set(
        fsDepts
          .filter(d => allowedIds.has(d.id))
          .map(d => DashboardComponent.deptNameToId(resolveHmDepartmentName(d.name)))
      );
      deptEntries = deptEntries.filter(d =>
        allowedNames.has(DashboardComponent.deptNameToId(d.name)) ||
        allowedNames.has(DashboardComponent.deptNameToId(d.shortName))
      );
    }

    return [...deptEntries]
      .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }))
      .map(d => {
      const deptProjects = this.deptMatching.projectsForDepartment(projects, d, deptEntries);
      const total = deptProjects.length;
      const onTrackCount = deptProjects.filter(p => this.isDeptProjectOnTrack(p, today)).length;
      const delayed = deptProjects.filter(p => this.isDeptProjectDelayed(p, today)).length;
      const critical = deptProjects.filter(p => this.isDeptProjectCritical(p, today)).length;
      const progress = total > 0 ? Math.round((onTrackCount / total) * 100) : 0;

      let health = 'Good';
      if (total === 0) health = 'N/A';
      else if (critical > 0 || progress < 40) health = 'Poor';
      else if (delayed > 0 || progress < 75) health = 'Fair';

      return {
        dept: d.shortName,
        label: d.name === 'Works' ? 'Works' : (d.shortName || d.name),
        name: d.name,
        icon: d.icon,
        color: d.color,
        count: total,
        progress,
        delayed,
        critical,
        health,
      };
    });
  });

  interventionTableMaxHeight = computed(() => {
    const count = this.highVisibilityProjects().length;
    if (count === 0) return null;
    const headerH = 32;
    const rowH = 36;
    const padding = 12;
    return Math.min(400, Math.max(88, headerH + count * rowH + padding));
  });

  private isDeptProjectDelayed(p: Project, today: Date): boolean {
    if (p.status === 'Completed') return false;
    if (!!(p as any).isDelayed) return true;
    if (p.status === 'Stuck') return true;
    const target = new Date(p.targetDate || p.end);
    if (isNaN(target.getTime())) return false;
    return Math.floor((today.getTime() - target.getTime()) / 86400000) > 60;
  }

  private isDeptProjectOnTrack(p: Project, today: Date): boolean {
    if (p.status === 'Completed') return true;
    if (this.isDeptProjectDelayed(p, today)) return false;
    return p.status === 'In Progress' || (p.physical ?? 0) >= 70;
  }

  private isDeptProjectCritical(p: Project, today: Date): boolean {
    if (p.status === 'Stuck') return true;
    if (this.isDeptProjectDelayed(p, today) && (p.physical ?? 0) < 30) return true;
    if (!!p.hmPriority && p.status !== 'Completed' && (p.physical ?? 0) < 30) return true;
    return false;
  }



    // Create-project modal state
    showNewProjectModal = signal(false);
    isSaving = signal(false);
    saveError = signal<string | null>(null);

    // Pending photos for new project (queued before project ID exists)
    pendingPhotoFiles   = signal<File[]>([]);
    pendingPhotoPreviews = signal<string[]>([]);
    isUploadingPhotos   = signal(false);

    // Cleanup: unknown (bad-import) departments
    isDeletingDept = signal<string | null>(null);
    deleteDeptError = signal<string | null>(null);
    unknownDeptGroups = computed(() => {
        const validDepts = new Set(this.departments);
        const groups = new Map<string, Project[]>();
        for (const p of this.allProjects()) {
            if (!p.dept || validDepts.has(p.dept)) continue;
            if (!groups.has(p.dept)) groups.set(p.dept, []);
            groups.get(p.dept)!.push(p);
        }
        return Array.from(groups.entries()).map(([dept, projects]: [string, Project[]]) => ({ dept, count: projects.length, ids: projects.map((p: Project) => p.id) }));

    });

    // Real-time clock signal
    currentTime = signal(new Date());
    private timerHandle: any;

    // ── Dynamic dashboard list modal ──
    listFilter = signal<
      'delayed60' | 'hmPending' | 'lowProgress' | 'systemStatus'
      | 'total' | 'sanctioned' | 'expenditure' | 'stuck' | 'lastUpdated'
      | 'hmOnTrack' | 'hmAtRisk' | 'hmDelayed' | 'hmTotal'
      | 'priorities' | 'recentUpdates' | 'reports'
      | 'completed' | 'inProgress' | 'planned'
      | null
    >(null);

    listTitle = computed(() => {
      switch (this.listFilter()) {
        case 'delayed60':     return 'Projects Delayed > 60 Days';
        case 'hmPending':     return 'HM Projects Pending Review';
        case 'lowProgress':   return 'Projects with Less than 30% Progress';
        case 'systemStatus':  return 'Active Projects (Monitoring Enabled)';
        case 'total':         return 'All Projects';
        case 'sanctioned':    return 'Sanctioned Budget — All Projects';
        case 'expenditure':   return 'Expenditure — Project-wise Funds Utilized';
        case 'stuck':         return 'Delayed / Stuck Projects';
        case 'lastUpdated':   return 'Recently Updated Projects';
        case 'hmTotal':       return 'HM Projects';
        case 'hmOnTrack':     return 'HM Projects — On Track';
        case 'hmAtRisk':      return 'HM Projects — At Risk';
        case 'hmDelayed':     return 'HM Projects — Delayed';
        case 'priorities':    return 'Priority Projects';
        case 'recentUpdates': return 'Recent Updates';
        case 'reports':       return 'All Projects (Reports)';
        case 'completed':     return 'Completed Projects';
        case 'inProgress':    return 'In-Progress Projects';
        case 'planned':       return 'Planned Projects';
        default:              return 'Projects';
      }
    });

    listProjects = computed<Project[]>(() => {
      const all = this.visibleProjects();
      const today = new Date();
      const isHm = (p: Project) =>
        p.priority || p.hmPriority ||
        p.visibility === 'HM Committed' || p.visibility === 'High Visibility';
      const isDelayedHm = (p: Project) =>
        !!(p as any).isDelayed ||
        p.status === 'Stuck' ||
        (p.targetDate && new Date(p.targetDate) < today) ||
        (p.end && new Date(p.end) < today);

      switch (this.listFilter()) {
        case 'delayed60':
          return all.filter(p => {
            if (p.status === 'Completed') return false;
            if (!!(p as any).isDelayed) return true;
            const target = new Date(p.targetDate || p.end);
            return Math.floor((today.getTime() - target.getTime()) / (1000 * 3600 * 24)) > 60;
          });
        case 'hmPending':
          return all.filter(p => p.hmPriority && (p.physical < 50 || p.status === 'Stuck'));
        case 'lowProgress':
          return all.filter(p => p.status !== 'Completed' && p.status !== 'Planned' && p.physical < 30);
        case 'systemStatus':
        case 'total':
        case 'sanctioned':
        case 'reports':
          return all;
        case 'expenditure':
          return [...all.filter(p =>
              p.status === 'In Progress' || p.status === 'Completed'
            )].sort((a, b) => (b.spent ?? 0) - (a.spent ?? 0));
        case 'stuck':
          return all.filter(p => p.status === 'Stuck');
        case 'lastUpdated':
        case 'recentUpdates':
          return [...all].sort((a, b) => {
            const da = new Date(a.photoUploadedAt || a.end || a.start || 0).getTime();
            const db = new Date(b.photoUploadedAt || b.end || b.start || 0).getTime();
            return db - da;
          }).slice(0, 50);
        case 'hmTotal':
          return all.filter(isHm);
        case 'hmDelayed':
          return all.filter(p => isHm(p) && isDelayedHm(p));
        case 'hmOnTrack':
          return all.filter(p => isHm(p) && !isDelayedHm(p) && (p.physical >= 70 || p.status === 'Completed'));
        case 'hmAtRisk':
          return all.filter(p => {
            if (!isHm(p)) return false;
            if (isDelayedHm(p)) return false;
            return !(p.physical >= 70 || p.status === 'Completed');
          });
        case 'priorities':
          return all.filter(p => p.recurringIntervention || p.priority || p.hmPriority);
        case 'completed':
          return all.filter(p => p.status === 'Completed');
        case 'inProgress':
          return all.filter(p => p.status === 'In Progress');
        case 'planned':
          return all.filter(p => p.status === 'Planned');
        default:
          return [];
      }
    });

    openList(filter: NonNullable<ReturnType<typeof this.listFilter>>): void {
      this.listFilter.set(filter);
    }

    closeList(): void {
      this.listFilter.set(null);
    }

    cardView = signal(true);
    toggleCardView(v: boolean): void { this.cardView.set(v); }

    // ── Mobile HM Drawer ──────────────────────────────────────────────
    isHmDrawerOpen = signal(false);
    private mobileHmChart: Chart | null = null;

    openHmDrawer(): void {
        this.isHmDrawerOpen.set(true);
        setTimeout(() => this.renderMobileHmChart(), 150);
    }

    closeHmDrawer(): void {
        this.isHmDrawerOpen.set(false);
    }

    private renderMobileHmChart(): void {
        const hmCtx = document.getElementById('hmProjectsChartMobile') as HTMLCanvasElement;
        if (!hmCtx) return;
        if (this.mobileHmChart) { this.mobileHmChart.destroy(); this.mobileHmChart = null; }
        const stats = this.hmStats();
        const hasData = stats.total > 0;
        this.mobileHmChart = new Chart(hmCtx, {
            type: 'doughnut',
            data: {
                labels: hasData ? ['On Track', 'At Risk', 'Delayed'] : ['No Data'],
                datasets: [{
                    data: hasData ? [stats.onTrack, stats.atRisk, stats.delayed] : [1],
                    backgroundColor: hasData ? ['#10b981', '#f59e0b', '#ef4444'] : ['#f1f5f9'],
                    borderWidth: 0,
                    hoverOffset: hasData ? 4 : 0,
                    weight: 1
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '70%',
                circumference: 360,
                rotation: 0,
                layout: { padding: 0 },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        enabled: hasData,
                        callbacks: {
                            label: (ctx) => {
                                const val = ctx.raw as number;
                                return ` ${ctx.label}: ${val} project${val !== 1 ? 's' : ''}`;
                            }
                        }
                    }
                }
            }
        });
    }

    // Run NAC migration only once per dashboard mount (prevents update→snapshot loops)

    // Excel import state
    activeModalTab = signal<'manual' | 'import'>('manual');
    excelRows = signal<ExcelImportRow[]>([]);
    isImporting = signal(false);
    importError = signal<string | null>(null);
    importProgress = signal<{ done: number; total: number } | null>(null);
    importDone = signal(false);
    isDragOver = signal(false);
    importSummary = signal<{ dept: string; schemes: { name: string; count: number }[] }[]>([]);
    excelValidCount = computed(() => this.excelRows().filter(r => r._valid).length);

    // Scheme dropdown state
    selectedDept = signal<string>('');
    availableSchemes = computed(() => this.schemesByDept[this.selectedDept()] ?? []);

    mmsy = inject(MMSYTRIPService);
    private dashStats        = inject(DashboardStatsService);
    private firestoreService = inject(FirestoreProjectService);
private authService     = inject(AuthService);
    private photoService    = inject(PhotoUploadService);
    private deptMasterService = inject(DepartmentMasterService);
    private firestoreDeptService = inject(FirestoreDepartmentService);
    private fb              = inject(FormBuilder);
    private router          = inject(Router);
    private destroy$        = new Subject<void>();

    isAdmin = computed(() => {
        const role = this.authService.currentUser()?.role;
        return role === UserRole.MINISTER || role === UserRole.ADMIN;
    });

    rdTotalProjects  = computed(() => this.rdSchemeData().reduce((sum: number, s: RDSchemeData) => sum + s.count, 0));
    rdTotalCost      = computed(() => this.rdSchemeData().reduce((sum: number, s: RDSchemeData) => sum + s.totalCost, 0));
    rdTotalSpent     = computed(() => this.rdSchemeData().reduce((sum: number, s: RDSchemeData) => sum + s.totalSpent, 0));
    rdTotalRoadLength = computed(() => this.rdSchemeData().reduce((sum: number, s: RDSchemeData) => sum + s.totalRoadLength, 0));



    Math = Math;

    deptChart: Chart | null = null;
    statusChart: Chart | null = null;

    newProjectForm!: FormGroup;

    private deptSchemesService = inject(DepartmentSchemesService);
    private deptMatching = inject(DepartmentMatchingService);
    
    // Legacy support for methods still using deptEntries
    get deptEntries(): any[] {
        return this.deptSchemesService.departmentsSignal() as any[];
    }


    // Reactive mapping of departments to their schemes
    readonly schemesByDept: Record<string, string[]> = Object.fromEntries(
        this.deptSchemesService.getDepartments().map((d: any) => [d.shortName, d.schemes.map((s: any) => s.name)])
    );

    readonly departments = Object.keys(this.schemesByDept);
    readonly statusOptions = ['Planned', 'In Progress', 'Completed', 'Stuck'];




    ngOnInit(): void {
        // ── Real-time Firestore KPI stats + project lists ──────────────
        this.firestoreService.getHmProjects$().pipe(takeUntil(this.destroy$)).subscribe({
            next: (projs) => {
                this.allProjects.set(projs);
                this.updateProjectLists(projs);
            },
            error: (err) => {
                console.warn('[dashboard] Firestore stream error:', err);
            }
        });

        // Load firestore departments to resolve IDs for filtering + canonical HM names
        this.firestoreDeptService.loadDepartmentsOnce().then(fsDepts => {
            if (fsDepts.length > 0) {
                this.deptSchemesService.mergeFromFirestore(
                    fsDepts.map(({ id: _id, ...rest }) => rest)
                );
            }
        }).catch(err => console.warn('[dashboard] department master load failed', err));

        this.deptMasterService.getDepartments().then(depts => {
            this.firestoreDepts.set(depts);
        });

        // Update real-time clock every minute
        this.timerHandle = setInterval(() => {
            this.currentTime.set(new Date());
        }, 60000);
    }

    private updateProjectLists(projects: Project[]): void {
        // allProjects is already set before calling this; visibleProjects() is a computed from it.
        const visible = this.visibleProjects();

        // Live project lists — scoped to what this role can see
        this.priorityProjects.set(visible.filter(p => p.recurringIntervention || p.priority || p.hmPriority));
        this.highVisibilityProjects.set(visible.filter(p =>
            p.visibility === 'High Visibility' ||
            p.hmPriority ||
            p.recurringIntervention
        ));
        this.hmCommittedProjects.set(visible.filter(p => p.visibility === 'HM Committed' || p.hmPriority));

        // Department breakdown (legacy consumers — keyed by canonical dept shortName)
        const today = new Date();
        const deptEntries = this.deptSchemesService.getDepartments();
        const grouped = this.deptMatching.groupByDepartment(visible, deptEntries);
        const liveBreakdown = deptEntries.map(d => {
            const deptProjects = grouped.get(d.name) ?? [];
            return {
                dept: d.shortName,
                total: deptProjects.length,
                onTrack: deptProjects.filter(p => this.isDeptProjectOnTrack(p, today)).length,
                delayed: deptProjects.filter(p => this.isDeptProjectDelayed(p, today)).length,
                critical: deptProjects.filter(p => this.isDeptProjectCritical(p, today)).length,
                hmTag: deptProjects.filter(p =>
                    p.visibility === 'High Visibility' ||
                    p.visibility === 'HM Committed' ||
                    p.recurringIntervention
                ).length,
            };
        });
        this.departmentBreakdown.set(liveBreakdown);
        this.renderCharts(visible);
    }

    ngAfterViewInit(): void {
        // Small delay lets the first Firestore snapshot arrive and populate allProjects()
        // before we render, so charts show live data rather than static fallback
        setTimeout(() => this.renderCharts(this.allProjects()), 300);
    }

    // ── Create project ──────────────────────────────────────────────────
    openNewProjectModal(tab: 'manual' | 'import' = 'manual'): void {
        this.activeModalTab.set(tab);
        this.excelRows.set([]);
        this.importError.set(null);
        this.importProgress.set(null);
        this.importDone.set(false);
        this.isDragOver.set(false);
        this.selectedDept.set('');
        this.newProjectForm = this.fb.group({
            name:     ['', [Validators.required, Validators.minLength(3)]],
            dept:     ['', Validators.required],
            loc:      ['', Validators.required],
            scheme:   ['', Validators.required],
            cost:     [null, [Validators.required, Validators.min(0)]],
            spent:    [0,   [Validators.min(0)]],
            physical: [0,   [Validators.min(0), Validators.max(100)]],
            status:               ['Planned', Validators.required],
            start:                [new Date().toISOString().split('T')[0], Validators.required],
            end:                  ['', Validators.required],
            priority:             [false],
            hmPriority:           [false],
            recurringIntervention:[false],
            remarks:              [''],
        });
        this.hmPriorityFlag.set(false);
        this.recurringFlag.set(false);
        // Update available schemes when dept changes
        this.newProjectForm.get('dept')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(d => {
                this.selectedDept.set(d ?? '');
                this.newProjectForm.get('scheme')?.setValue('');
            });
        // Sync checkbox signals and implement mutual exclusion
        this.newProjectForm.get('hmPriority')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(v => {
                this.hmPriorityFlag.set(!!v);
                if (v) {
                    this.newProjectForm.get('recurringIntervention')?.setValue(false, { emitEvent: false });
                    this.recurringFlag.set(false);
                }
            });
        this.newProjectForm.get('recurringIntervention')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(v => {
                this.recurringFlag.set(!!v);
                if (v) {
                    this.newProjectForm.get('hmPriority')?.setValue(false, { emitEvent: false });
                    this.hmPriorityFlag.set(false);
                }
            });
        this.saveError.set(null);
        this.showNewProjectModal.set(true);
    }

    closeNewProjectModal(): void {
        // Revoke any blob URLs created for photo previews
        this.pendingPhotoPreviews().forEach(u => URL.revokeObjectURL(u));
        this.pendingPhotoFiles.set([]);
        this.pendingPhotoPreviews.set([]);
        this.showNewProjectModal.set(false);
        this.excelRows.set([]);
        this.importProgress.set(null);
        this.importDone.set(false);
        this.importSummary.set([]);
    }

    // ── Pending photos for new project ────────────────────────────────────
    onCreateModalPhotoSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files ?? []);
        input.value = '';

        const valid = files.filter(f => f.type.startsWith('image/') && f.size <= 10 * 1024 * 1024);
        if (valid.length === 0) return;

        const newPreviews = valid.map(f => URL.createObjectURL(f));
        this.pendingPhotoFiles.update(list => [...list, ...valid]);
        this.pendingPhotoPreviews.update(list => [...list, ...newPreviews]);
    }

    removePendingPhoto(index: number): void {
        URL.revokeObjectURL(this.pendingPhotoPreviews()[index]);
        this.pendingPhotoFiles.update(list => list.filter((_, i) => i !== index));
        this.pendingPhotoPreviews.update(list => list.filter((_, i) => i !== index));
    }

    async createProject(): Promise<void> {
        if (this.newProjectForm.invalid) {
            this.newProjectForm.markAllAsTouched();
            return;
        }
        this.isSaving.set(true);
        this.saveError.set(null);
        try {
            const v = this.newProjectForm.value;
            const id = await this.firestoreService.createProject({
                name: v.name, dept: v.dept, loc: v.loc,
                cost: Number(v.cost), spent: Number(v.spent ?? 0),
                physical: Number(v.physical ?? 0),
                status: v.status, start: v.start, end: v.end,
                priority: v.priority ?? false,
                hmPriority: v.hmPriority ?? false,
                recurringIntervention: v.recurringIntervention ?? false,
                remarks: v.remarks ?? '',
                scheme: v.scheme ?? ''
            });

            // Upload any pending photos now that we have a real project ID
            const files = this.pendingPhotoFiles();
            if (files.length > 0) {
                this.isUploadingPhotos.set(true);
                try {
                    const urls = await Promise.all(
                        files.map(f => this.photoService.uploadPhoto(id, f))
                    );
                    await this.firestoreService.updateProject(id, { photos: urls });
                } catch (photoErr) {
                    console.warn('Photo upload during creation failed:', photoErr);
                    // Non-fatal: project is already created, navigate anyway
                } finally {
                    this.isUploadingPhotos.set(false);
                }
            }

            this.closeNewProjectModal();
            this.router.navigate(['/dhamnagar-dashboard/project', id]);
        } catch (err: unknown) {
            console.error('createProject error:', err);
            const code = (err as { code?: string })?.code;
            if (code === 'permission-denied') {
                this.saveError.set('Permission denied. Firestore security rules may not be deployed. Run: firebase deploy --only firestore:rules');
            } else if (code === 'unauthenticated') {
                this.saveError.set('Not authenticated. Please log out and log back in.');
            } else if (code === 'unavailable' || (err as Error)?.message?.includes('timed out')) {
                this.saveError.set('Firestore is unreachable. Check your internet connection.');
            } else {
                this.saveError.set(`Failed to create project: ${(err as Error)?.message ?? 'Unknown error'}`);
            }
        } finally {
            this.isSaving.set(false);
        }
    }

    openDepartmentDetails(deptName: string): void {
        this.router.navigate(['/dhamnagar-dashboard/departments'], { 
            queryParams: { dept: deptName } 
        });
    }

    openProjectDetails(projectId: string): void {
        this.router.navigate(['/dhamnagar-dashboard/project', projectId]);
    }

    // ── Excel import ────────────────────────────────────────────────────
    onDragOver(e: DragEvent): void {
        e.preventDefault();
        this.isDragOver.set(true);
    }

    onDragLeave(): void {
        this.isDragOver.set(false);
    }

    onDrop(e: DragEvent): void {
        e.preventDefault();
        this.isDragOver.set(false);
        const file = e.dataTransfer?.files?.[0];
        if (file) this.processExcelFile(file);
    }

    onExcelFileInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (file) this.processExcelFile(file);
        input.value = '';
    }

    private processExcelFile(file: File): void {
        this.importError.set(null);
        this.importDone.set(false);
        this.importProgress.set(null);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target!.result as ArrayBuffer);
                const wb = XLSX.read(data, { type: 'array', cellDates: true });
                const allRows: ExcelImportRow[] = [];

                for (const sheetName of wb.SheetNames) {
                    const ws = wb.Sheets[sheetName];
                    // Read everything as a raw 2D array (avoids any ambiguity with range/header options)
                    const raw2d = XLSX.utils.sheet_to_json<unknown[]>(ws, {
                        header: 1,
                        defval: ''
                    }) as unknown[][];

                    const headerRowIndex = this.detectHeaderRow(raw2d);
                    if (headerRowIndex === -1 || headerRowIndex >= raw2d.length - 1) continue;

                    // Original header strings (un-normalized) — e.g. "NAME OF PROJECT", "ESTMATE COST"
                    const headers = (raw2d[headerRowIndex] as unknown[]).map(h => String(h ?? '').trim());

                    // Build one record per data row by pairing headers[i] → cell[i]
                    for (let rowIdx = headerRowIndex + 1; rowIdx < raw2d.length; rowIdx++) {
                        const cells = raw2d[rowIdx] as unknown[];
                        // Skip completely blank rows
                        if (cells.every(c => c === '' || c === null || c === undefined)) continue;

                        const record: Record<string, unknown> = {};
                        headers.forEach((h, colIdx) => {
                            if (h) record[h] = cells[colIdx] ?? '';
                        });

                        allRows.push(this.parseExcelRow(record, sheetName));
                    }
                }

                this.excelRows.set(allRows);
                if (allRows.length === 0) {
                    this.importError.set('No data rows found. Make sure the file has headers like "NAME OF PROJECT", "ESTMATE COST", "STATUS".');
                }
            } catch (err) {
                console.error('Excel parse error:', err);
                const msg = (err as Error)?.message;
                this.importError.set(msg
                    ? `Failed to parse file: ${msg}`
                    : 'Failed to read file. Ensure it is a valid .xlsx or .xls file.');
            }
        };
        reader.readAsArrayBuffer(file);
    }

    /** Scan up to 15 rows and return the row with the MOST keyword matches.
     *  This avoids false-positives when a title row accidentally contains 1-2 keywords. */
    private detectHeaderRow(rows: unknown[][]): number {
        const keywords = ['project', 'scheme', 'name', 'cost', 'status', 'village',
                          'dept', 'location', 'loc', 'remarks', 'executant', 'sl', 'serial'];
        let bestIdx = -1;
        let bestScore = 1; // require at least 2 matches
        for (let i = 0; i < Math.min(rows.length, 15); i++) {
            const rowStr = (rows[i] as unknown[] ?? [])
                .map(c => String(c ?? '').toLowerCase())
                .join(' ');
            const score = keywords.filter(k => rowStr.includes(k)).length;
            if (score > bestScore) {
                bestScore = score;
                bestIdx = i;
            }
        }
        // Fallback: if nothing qualifies, use row 0
        return bestIdx === -1 ? (rows.length > 0 ? 0 : -1) : bestIdx;
    }

    private toDateString(val: unknown): string {
        if (!val) return '';
        if (val instanceof Date) return val.toISOString().split('T')[0];
        if (typeof val === 'string') {
            const ddmm = val.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/);
            if (ddmm) return `${ddmm[3]}-${ddmm[2]}-${ddmm[1]}`;
            if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
        }
        return '';
    }

    /** Normalise a raw scheme string for matching. */
    private normStr(s: string): string {
        return s.toLowerCase().replace(/[\s\-_\/\.]+/g, ' ').trim();
    }

    /** Given a raw scheme name and the inferred dept shortName, return the canonical
     *  scheme name as stored in DepartmentSchemesService (e.g. "BGBO(25-26)" → "Bikashita Gaon Bikashita Odisha (BGBO)").
     *  Returns the original string if no match is found. */
    private canonicalizeScheme(rawScheme: string, deptShortName: string): string {
        if (!rawScheme || !deptShortName) return rawScheme;
        const norm = this.normStr(rawScheme);
        const deptEntry = (this.deptEntries as any[]).find((d: any) => d.shortName === deptShortName);
        if (!deptEntry) return rawScheme;
        for (const scheme of deptEntry.schemes) {

            const schNorm = this.normStr(scheme.name);
            if (schNorm === norm || schNorm.includes(norm)) return scheme.name;
            const ac = scheme.name.match(/\(([A-Z][A-Z0-9]+)\)/);
            if (ac) {
                const a = ac[1].toLowerCase();
                if (norm === a || norm.startsWith(a + ' ') || norm.startsWith(a + '-') || norm.startsWith(a + '('))
                    return scheme.name;
            }
            const fw = schNorm.split(' ')[0];
            if (fw.length >= 4 && norm.startsWith(fw)) return scheme.name;
        }
        return rawScheme;
    }

    /** Given a scheme name from an Excel import, find the matching department shortName
     *  by comparing against all scheme names registered in DepartmentSchemesService. */
    private inferDeptFromScheme(schemeName: string): string {
        if (!schemeName) return '';
        const norm = schemeName.toLowerCase().replace(/[\s\-_\/\.]+/g, ' ').trim();

        for (const dept of this.deptEntries as any[]) {
            for (const scheme of dept.schemes) {

                const schNorm = scheme.name.toLowerCase().replace(/[\s\-_\/\.]+/g, ' ').trim();

                // 1. Exact match
                if (schNorm === norm) return dept.shortName;

                // 2. The Excel value is contained in the full scheme name (handles abbreviations like "CMSA" vs "CM Special Assistance (CMSA)")
                if (schNorm.includes(norm)) return dept.shortName;

                // 3. Acronym in parentheses: e.g. scheme.name = "CM Special Assistance (CMSA)" → match "cmsa..."
                const acMatch = scheme.name.match(/\(([A-Z][A-Z0-9]+)\)/);
                if (acMatch) {
                    const ac = acMatch[1].toLowerCase();
                    if (norm === ac || norm.startsWith(ac + ' ') || norm.startsWith(ac + '-') || norm.startsWith(ac + '(')) {
                        return dept.shortName;
                    }
                }

                // 4. First significant word of scheme name matches start of Excel value (e.g. "PMGSY" in "PMGSY (I, II, III, IV)")
                const firstWord = schNorm.split(' ')[0];
                if (firstWord.length >= 4 && norm.startsWith(firstWord)) return dept.shortName;
            }
        }

        // 5. GP-level common pattern matching not covered by the service scheme list
        //    SFC (State Finance Commission grants) and DANA (District Annual grants) → PR&DW
        if (/^5th sfc|^sfc|\bsfc\b/.test(norm) ||
            /^dana/.test(norm)) {
            return 'PR&DW';
        }
        return '';
    }

    private normalizeStatus(s: string): string {
        const map: Record<string, string> = {
            'planned': 'Planned',
            'n/s': 'Planned', 'not started': 'Planned', 'not_started': 'Planned', 'ns': 'Planned',
            'in progress': 'In Progress', 'inprogress': 'In Progress', 'in-progress': 'In Progress',
            'ip': 'In Progress', 'i/p': 'In Progress', 'ongoing': 'In Progress', 'running': 'In Progress',
            'completed': 'Completed', 'done': 'Completed', 'finish': 'Completed', 'finished': 'Completed',
            'c': 'Completed', 'comp': 'Completed',
            'stuck': 'Stuck', 'delayed': 'Stuck', 'blocked': 'Stuck', 'held': 'Stuck'
        };
        return map[s.toLowerCase().trim()] ?? 'Planned';
    }

    private parseExcelRow(r: Record<string, unknown>, sheetName = ''): ExcelImportRow {
        // Normalize all keys: lowercase, collapse whitespace to underscore, strip special chars
        const row: Record<string, unknown> = {};
        for (const k of Object.keys(r)) {
            const normalized = k.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
            row[normalized] = r[k];
        }

        // Name: standard key OR "NAME OF PROJECT" → name_of_project
        const name = String(
            row['name'] ?? row['name_of_project'] ?? row['project_name'] ?? row['nameofproject'] ?? ''
        ).trim();

        // Scheme first — needed to infer dept when no explicit dept column is present
        // "Name of Scheme" → name_of_scheme
        const scheme = String(
            row['scheme'] ?? row['name_of_scheme'] ?? row['scheme_name'] ?? ''
        ).trim();

        // Dept: explicit column → infer from scheme name → fall back to sheet name (GP name etc.)
        const rawDept = String(row['dept'] ?? row['department'] ?? '').trim();
        const dept = rawDept || this.inferDeptFromScheme(scheme) || sheetName;

        // Canonicalize scheme to its exact service name so Departments page can match it
        const canonScheme = this.canonicalizeScheme(scheme, dept);

        // Location: standard key OR "Name of Village" → name_of_village
        const loc = String(
            row['loc'] ?? row['location'] ?? row['name_of_village'] ?? row['village'] ?? ''
        ).trim();

        // Cost: standard key OR "ESTMATE COST" / "ESTIMATE COST"
        const costRaw = row['cost'] ?? row['estmate_cost'] ?? row['estimate_cost'] ??
                        row['estimated_cost'] ?? row['sanctioned_cost'] ?? 0;
        const cost = isNaN(Number(costRaw)) ? 0 : Number(costRaw);

        const spent  = isNaN(Number(row['spent']  ?? '')) ? 0 : Number(row['spent']  ?? 0);
        const physical = isNaN(Number(row['physical'] ?? row['progress'] ?? '')) ? 0
                       : Number(row['physical'] ?? row['progress'] ?? 0);

        // Status
        const rawStatus = String(row['status'] ?? 'N/S');
        const status = this.normalizeStatus(rawStatus);

        // Dates — optional in this format; default to empty
        const start = this.toDateString(row['start'] ?? row['start_date'] ?? '');
        const end   = this.toDateString(row['end'] ?? row['end_date'] ?? row['target_completion'] ?? row['completion_date'] ?? '');

        // Priority
        const rawPriority = row['priority'] ?? '';
        const priority = rawPriority === true || String(rawPriority).toLowerCase() === 'yes' || rawPriority === 1;

        // Remarks
        const remarks = String(row['remarks'] ?? '').trim();

        // Executing agency: "Name of the Executant" → name_of_the_executant
        const executingAgency = String(
            row['executingagency'] ?? row['executing_agency'] ??
            row['name_of_the_executant'] ?? row['executant'] ?? row['contractor'] ?? ''
        ).trim();

        // Validation — ALL four fields are mandatory for import
        const errors: string[] = [];
        if (!name || name.length < 3)   errors.push('Project name required (min 3 chars)');
        if (!scheme)                     errors.push('Name of scheme is required');
        if (!loc)                        errors.push('Village / location is required');
        if (isNaN(cost) || cost <= 0)    errors.push('Estimate cost must be > 0');
        if (!['Planned', 'In Progress', 'Completed', 'Stuck'].includes(status)) {
            errors.push(`Unknown status "${rawStatus}"`);
        }

        return {
            name, dept, loc, scheme: canonScheme, cost, spent, physical,
            status, start, end, priority, remarks, executingAgency,
            _sheet: sheetName, _valid: errors.length === 0, _errors: errors
        };
    }

    removeExcelRow(index: number): void {
        this.excelRows.update(rows => rows.filter((_, i) => i !== index));
    }

    /** Used in template to sum scheme counts in import summary */
    readonly sumCount = (acc: number, s: { count: number }) => acc + s.count;

    async importExcelProjects(): Promise<void> {
        const valid = this.excelRows().filter(r => r._valid);
        if (!valid.length) return;
        this.isImporting.set(true);
        this.importError.set(null);
        this.importProgress.set({ done: 0, total: valid.length });
        let done = 0;
        try {
            for (const row of valid) {
                await this.firestoreService.createProject({
                    name: row.name,
                    dept: row.dept,
                    loc: row.loc || row._sheet,
                    scheme: row.scheme || undefined,
                    cost: row.cost,
                    spent: row.spent,
                    physical: row.physical,
                    status: row.status as 'Planned' | 'In Progress' | 'Completed' | 'Stuck',
                    start: row.start,
                    end: row.end,
                    priority: row.priority,
                    remarks: row.remarks,
                    executingAgency: row.executingAgency || undefined,
                });
                done++;
                this.importProgress.set({ done, total: valid.length });
            }
            // Build summary grouped by dept → scheme
            const deptMap = new Map<string, Map<string, number>>();
            for (const row of valid) {
                const dept = row.dept || 'Unknown';
                const scheme = row.scheme || 'No Scheme';
                if (!deptMap.has(dept)) deptMap.set(dept, new Map());
                const schMap = deptMap.get(dept)!;
                schMap.set(scheme, (schMap.get(scheme) ?? 0) + 1);
            }
            this.importSummary.set(
                Array.from(deptMap.entries()).map(([dept, schMap]) => ({
                    dept,
                    schemes: Array.from(schMap.entries()).map(([name, count]) => ({ name, count }))
                }))
            );
            this.importDone.set(true);
            // No auto-close — user reads the summary and clicks Done
        } catch (err: unknown) {
            const code = (err as { code?: string })?.code;
            if (code === 'permission-denied') {
                this.importError.set('Permission denied — check Firestore rules.');
            } else {
                this.importError.set(`Import failed: ${(err as Error)?.message ?? 'Unknown error'}`);
            }
        } finally {
            this.isImporting.set(false);
        }
    }

    downloadExcelTemplate(): void {
        // Sheet 1: Standard format
        const ws1 = XLSX.utils.aoa_to_sheet([
            ['name', 'dept', 'loc', 'scheme', 'cost', 'spent', 'physical', 'status', 'start', 'end', 'priority', 'remarks', 'executingAgency'],
            ['Road Widening Project', 'RD', 'Bhadrak', 'PMGSY', 5000, 1000, 20, 'In Progress', '2025-01-01', '2025-12-31', 'no', 'Sample remarks', ''],
            ['Water Supply Scheme', 'RWSS', 'Dhamnagar Block', 'JJM', 12000, 2000, 35, 'Planned', '2025-04-01', '2026-03-31', 'yes', '', '']
        ]);
        ws1['!cols'] = [{ wch: 40 }, { wch: 10 }, { wch: 20 }, { wch: 16 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 25 }, { wch: 25 }];

        // Sheet 2: GP-style format (matches the real Excel with title + headers)
        const ws2 = XLSX.utils.aoa_to_sheet([
            ['NOT STARTED PROJECTS OF SAMPLE GP'],
            ['Sl. No', 'Name of Village', 'Name of Scheme', 'NAME OF PROJECT', 'ESTMATE COST', 'STATUS', 'Name of the Executant', 'Contact No.', 'REMARKS'],
            [1, 'SampleVillage', '5th SFC (24-25)', 'Construction of community hall', 350000, 'N/S', '', '', ''],
            [2, 'SampleVillage', 'CMSA(24-25)', 'Renovation of school boundary wall', 200000, 'N/S', '', '', '']
        ]);
        ws2['!cols'] = [{ wch: 8 }, { wch: 16 }, { wch: 16 }, { wch: 50 }, { wch: 14 }, { wch: 10 }, { wch: 25 }, { wch: 14 }, { wch: 20 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws1, 'Standard Format');
        XLSX.utils.book_append_sheet(wb, ws2, 'GP Format (sample)');
        XLSX.writeFile(wb, 'project_import_template.xlsx');
    }

    navigateToStuck(): void {
        this.router.navigate(['/dhamnagar-dashboard/stuck']);
    }

    goToHome(): void {
        this.router.navigate(['/home']);
    }

    navigateToDepartment(deptName: string): void {
        this.router.navigate(['/dhamnagar-dashboard/departments'], { queryParams: { dept: deptName } });
    }

    getDeptBgClass(color: string): string {
        const map: Record<string, string> = {
            red:    'bg-red-500',
            blue:   'bg-blue-500',
            purple: 'bg-purple-500',
            amber:  'bg-amber-500',
            cyan:   'bg-cyan-500',
            violet: 'bg-violet-500',
            green:  'bg-green-600',
            orange: 'bg-orange-500',
            teal:   'bg-teal-500',
            pink:   'bg-pink-500',
            indigo: 'bg-indigo-500',
            yellow: 'bg-yellow-500',
        };
        return map[color] ?? 'bg-slate-500';
    }

    getDeptLiveTotal(shortName: string): number {
        return this.departmentBreakdown().find((d: any) => d.dept === shortName)?.total
            ?? (this.deptSchemesService.departmentsSignal().find((d: any) => d.shortName === shortName) as any)?.schemes
                .reduce((s: number, sc: any) => s + sc.projects, 0)
            ?? 0;
    }




    getStatusColor(status: string): string {


        switch (status) {
            case 'In Progress': return '#3b82f6';
            case 'Completed':   return '#10b981';
            case 'Stuck':       return '#ef4444';
            case 'Planned':     return '#9ca3af';
            default:            return '#6b7280';
        }
    }

    getVisibilityBadgeColor(visibility?: string): string {
        if (visibility === 'HM Committed')    return 'bg-blue-100 text-blue-800 border-blue-300';
        if (visibility === 'High Visibility') return 'bg-purple-100 text-purple-800 border-purple-300';
        return '';
    }

    renderCharts(projects: Project[] = []): void {
        // Use setTimeout to ensure Angular has finished DOM updates (essential for OnPush)
        setTimeout(() => {
            // ── Department Expenditure Bar Chart ───────────────────────────
            const deptSpendMap: Record<string, number> = {};
            for (const p of projects) {
                const dept = p.dept || 'Unknown';
                deptSpendMap[dept] = (deptSpendMap[dept] ?? 0) + (p.spent ?? 0);
            }

            const ctx1 = document.getElementById('deptChart') as HTMLCanvasElement;
            if (ctx1) {
                if (this.deptChart) { this.deptChart.destroy(); this.deptChart = null; }
                this.deptChart = new Chart(ctx1, {
                    type: 'bar',
                    data: {
                        labels: Object.keys(deptSpendMap),
                        datasets: [{
                            label: 'Expenditure (₹L)',
                            data: Object.values(deptSpendMap),
                            backgroundColor: '#4f46e5',
                            borderRadius: 4
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false } },
                        scales: { y: { beginAtZero: true, grid: { display: false } } }
                    }
                });
            }

            // ── HM Projects Donut Chart ──────────────────────────────────────
            const hmCtx = document.getElementById('hmProjectsChart') as HTMLCanvasElement;
            if (hmCtx) {
                if (this.statusChart) {
                    this.statusChart.destroy();
                    this.statusChart = null;
                }
                const stats = this.hmStats();
                
                // If no data, show a grey ring
                const hasData = stats.total > 0;
                const chartData = hasData 
                    ? [stats.onTrack, stats.atRisk, stats.delayed]
                    : [0, 0, 0, 1]; // Single grey segment
                const chartColors = hasData
                    ? ['#10b981', '#f59e0b', '#ef4444']
                    : ['#f1f5f9'];

                this.statusChart = new Chart(hmCtx, {
                    type: 'doughnut',
                    data: {
                        labels: hasData ? ['On Track', 'At Risk', 'Delayed'] : ['No Data'],
                        datasets: [{
                            data: chartData,
                            backgroundColor: chartColors,
                            borderWidth: 0,
                            hoverOffset: hasData ? 4 : 0,
                            weight: 1
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: '70%',
                        circumference: 360,
                        rotation: 0,
                        layout: {
                            padding: 0
                        },
                        plugins: {
                            legend: { display: false },
                            tooltip: {
                                enabled: hasData,
                                callbacks: {
                                    label: (ctx) => {
                                        const val = ctx.raw as number;
                                        return ` ${ctx.label}: ${val} project${val !== 1 ? 's' : ''}`;
                                    }
                                }
                            }
                        }
                    }
                });
            }
        }, 100);
    }

    // ── Cleanup: delete all projects for an unknown/bad-import department ─
    async deleteDeptProjects(dept: string, ids: string[]): Promise<void> {
        if (!ids.length) return;
        this.isDeletingDept.set(dept);
        this.deleteDeptError.set(null);
        try {
            for (const id of ids) {
                await this.firestoreService.deleteProject(id);
            }
        } catch (err: unknown) {
            this.deleteDeptError.set(`Failed to delete: ${(err as Error)?.message ?? 'Unknown error'}`);
        } finally {
            this.isDeletingDept.set(null);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.timerHandle) clearInterval(this.timerHandle);
        this.deptChart?.destroy();
        this.statusChart?.destroy();
        this.mobileHmChart?.destroy();
    }
}
// Heartbeat: 2026-05-09 23:02

